import { FloatingPortal, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@poseidon/components/cn';
import { Button, Tooltip } from '@poseidon/components/Input2';
import type { TipTapDocument, TipTapVariable } from '@poseidon/components/Input2/TipTapInput';
import type { ReminderAction } from '@poseidon/components/ManualSelect';
import { General } from '@poseidon/components/StrokeIcons';
import { sequenceOverrideKey } from '@poseidon/types/types/sequenceOverrideKey';

import type { SequenceFace } from '~/components/Sequence/SequenceNodeFaces';
import type { SequenceContainerStepType, SequenceStep, SequenceStepType } from '~/components/Sequence/sequenceSteps';
import { useSequenceFaceActions } from '@/hooks/useSequenceFaceActions';
import { useSequenceFaces } from '@/hooks/useSequenceFaces';
import { api } from '@/utils/api';
import { SequenceGraphInsert } from '~/components/Sequence/SequenceGraphInsert';
import { SequenceGraphNode } from '~/components/Sequence/SequenceGraphNode';
import { computeBranchLanes, spineNodeHalfWidth } from '~/components/Sequence/sequenceLayout';
import type { WaitUnit } from '~/components/Sequence/WaitDurationInput';
import { SequenceNodeFaces } from '~/components/Sequence/SequenceNodeFaces';
import { SequenceNodePreview } from '~/components/Sequence/SequenceNodePreview';
import { SequencePanZoom } from '~/components/Sequence/SequencePanZoom';
import { SequenceParallelArms } from '~/components/Sequence/SequenceParallelArms';
import { SequenceSwitchBranches, switchFanFlairWidth } from '~/components/Sequence/SequenceSwitchBranches';
import { SequenceSwitchNode } from '~/components/Sequence/SequenceSwitchNode';
import { SequencePreStepDetail } from '~/components/Sequence/SequencePreStepDetail';
import { SequencePreStepNode } from '~/components/Sequence/SequencePreStepNode';
import { SequenceStepEditor } from '~/components/Sequence/SequenceStepEditor';
import { SequenceStepPreview, stepHasContentPreview } from '~/components/Sequence/SequenceStepPreview';
import {
  canMergeIntoGroup,
  canReceiveMerge,
  extractArmToSpine,
  insertDisabledReason,
  insertSpineStep,
  isJourneyDoneNode,
  mergeArmIntoNode,
  newSequenceStep,
  newSwitchStep,
  PRE_STEP_NODE_IDS,
  previousEmailSubject,
  SEQUENCE_PRE_STEPS,
  sequenceStepIssue,
  manualTaskWaitNodeForStepType,
  STEP_META,
  stepFaceNodeIds,
  stepIsEditable,
  stepOpensEditorOnInsert,
  SWITCH_FAN_COL_W,
} from '~/components/Sequence/sequenceSteps';
import type { DropTarget, LineAddr, StepAddr } from '~/components/Sequence/sequenceMove';
import { canDrop, moveStep, parseLine, sameLine, serializeLine, stepAt } from '~/components/Sequence/sequenceMove';

interface Props {
  teamId: string;
  sequenceId: string;
  initialSteps: SequenceStep[];
  /** Rich-editor mention data (`@`-variables + `/`-documents) threaded to each step's content editor. */
  variables?: TipTapVariable[];
  documents?: TipTapDocument[];
  /** The team's reusable reminder actions + create hook — threaded to the `reminder` step's dropdown. */
  reminderActions?: ReminderAction[];
  reminderActionsLoading?: boolean;
  onCreateReminder?: (name: string) => Promise<void>;
  reminderCreating?: boolean;
  /**
   * LOCKED per-contact mode (the contact detail page). Freezes the structure — no drag, no `+` insert, no
   * delay / reorder / add / remove — so the graph shows THIS contact's sequence, and clicking a send step
   * edits only its body/subject as a per-contact override (saved via {@link onSaveStepContent}, never to the
   * template). Off (the default) is the full authoring editor, unchanged.
   */
  readOnly?: boolean;
  /** Per-contact content overrides to overlay, keyed by {@link sequenceOverrideKey} (readOnly mode). */
  overrides?: Record<string, { body?: string; subject?: string }>;
  /** Per-step progress, keyed by {@link sequenceOverrideKey} — highlights the current step + locks sent ones. */
  stepStatus?: Record<string, 'sent' | 'current' | 'upcoming'>;
  /** Persist one step's per-contact body/subject override (readOnly mode). Called on editor close when changed. */
  onSaveStepContent?: (overrideKey: string, next: { body?: string; subject?: string }) => Promise<void>;
  /**
   * readOnly mode: mark the call/reminder to-do this contact is currently parked on done (advances the arm
   * past the manual step). Called with the step's manual-task wait node id ({@link manualTaskWaitNodeForStepType}).
   * A "Mark done" control appears only on a `current` call/reminder spine step. Wire to `sequence.completeTask`.
   */
  onCompleteManualStep?: (nodeId: string) => void;
}

// Canvas layout (content-space px). Nodes are centered on a vertical spine; insert slots sit at the
// gap midpoints between them. The automatic pre-steps (research/dossier/qualify/enroll) sit above
// the editable steps.
const CX = 200;
const NODE_H = 40;
const GAP = 44;
const STRIDE = NODE_H + GAP;
// The not-accepted arm (Withdraw → …) sits this much further right than its lane slot so the wide
// "IF NOT ACCEPTED" edge label fits ON the connector between the await pill and the Withdraw node without
// overlapping either. Kept comfortably below LANE_W so the arm still stays inside its own branch lane.
const NOT_ACCEPTED_ARM_INSET = 90;
// Half-width (px) of the fixed "Withdraw invite" pill — the not-accepted arm's first node — used to center
// the edge label in the clear span between the await pill and Withdraw. (A fixed-content node, so a
// constant is safe here; nothing else depends on it.)
const WITHDRAW_HALF_W = 70;
const PRE_TOP = STRIDE; // center-y of the first pre-step
const preCenterY = (p: number) => PRE_TOP + p * STRIDE;
// First editable node center — an extra gap below the last pre-step ("Enroll") marks the handoff.
const EDIT_TOP = preCenterY(SEQUENCE_PRE_STEPS.length - 1) + Math.round(STRIDE * 1.5);
const nodeCenterY = (i: number) => EDIT_TOP + i * STRIDE;
const slotCenterY = (k: number) => EDIT_TOP - STRIDE / 2 + k * STRIDE;
// A switch fans its route branches DOWN below the node (the attribute toggle lives on the spine pill).
// These estimate a branch step's y (from the switch node's row) and the fan's width, so the content
// editor opens beside the right row and clears the fan. Column pitch is shared (SWITCH_FAN_COL_W).
const SWITCH_BELOW = 18; // switch node CENTER → top of the branch fan (just below the pill's lower edge)
const SWITCH_HEADER = 96; // switch node row → a branch's first step (below-gap + connector band + chip)
const SWITCH_ROW = 76; // per additional branch step
// Columns = [Everything-else slot] + branches + [+ range tile]; over-reserve at branches+2 (the else slot
// is one column whether it's the catch-all branch or the "+ Everything else" tile).
const switchFanWidth = (step: SequenceStep): number =>
  ((step.switch?.branches.length ?? 0) + 2) * SWITCH_FAN_COL_W + switchFanFlairWidth(step);
// The FIRST range branch (column 1) sits ON the spine (under the node); the Everything-else slot is the
// one column to its LEFT — so the fan starts 1.5 columns left of CX.
const switchFanLeft = (cx: number): number => cx - 1.5 * SWITCH_FAN_COL_W;

// A content step's authored text is previewed in the gutter to the RIGHT of its node (the people-piles
// sit on the left). Each preview hugs its OWN node's right edge (not the widest node on the spine) so
// the text sits right next to the step it belongs to.
const PREVIEW_GAP = 12; // node right edge → preview left
const PREVIEW_W = 224; // default text width (w-56); capped narrower when branch lanes claim the gutter

// What's being dragged: a spine node, or an arm (chip) being pulled OUT of a parallel group.
type DragSource =
  | { kind: 'spine'; index: number }
  | { kind: 'arm'; group: number; arm: number }
  | { kind: 'spawnArm'; group: number; arm: number }
  | { kind: 'switchArm'; group: number; branch: number; arm: number }
  // A step nested inside a container (group/spawn) that sits in a switch branch arm: switch step
  // `group` → branch `branch` → the container at `slot` → its arm step `arm`.
  | { kind: 'switchArmChild'; group: number; branch: number; slot: number; arm: number };
// What's being dragged in the unified cross-surface drag: any LINE step (spine / switch branch / spawn
// arm), addressed by {@link StepAddr}, or a group-arm chip being pulled OUT of a parallel group. The drop
// (a {@link DropTarget}) and the move itself live in sequenceMove.
type DragMove = { kind: 'lineStep'; addr: StepAddr } | { kind: 'arm'; group: number; arm: number };
// A node is dragged only once the pointer travels this many px from where it went down. Below it the
// gesture is treated as a click (→ open the step editor); at/above it the drag arms (ghost + drop).
const DRAG_THRESHOLD = 5;

/** Resolve the step a {@link DragSource} points at, from a given steps array. */
function stepForSource(source: DragSource, arr: SequenceStep[]): SequenceStep | null {
  if (source.kind === 'spine') return arr[source.index] ?? null;
  if (source.kind === 'spawnArm') return arr[source.group]?.arm?.[source.arm] ?? null;
  if (source.kind === 'switchArm') return arr[source.group]?.switch?.branches[source.branch]?.arm?.[source.arm] ?? null;
  if (source.kind === 'switchArmChild') {
    const container = arr[source.group]?.switch?.branches[source.branch]?.arm?.[source.slot];
    if (container?.type === 'spawn') return container.arm?.[source.arm] ?? null;
    if (container?.type === 'group') return container.arms?.[source.arm]?.[0] ?? null;
    return null;
  }
  return arr[source.group]?.arms?.[source.arm]?.[0] ?? null; // group arm
}

/**
 * The array of sibling steps the edited step lives in (its spine or arm) + its index within it — used to
 * find the previous email step for the email editor's "continue thread" toggle. Mirrors
 * {@link stepForSource}'s traversal; single-step arms (group arm) yield a one-element array (index 0), so
 * there's simply no earlier email to thread onto there.
 */
function siblingsForSource(source: DragSource, arr: SequenceStep[]): { siblings: SequenceStep[]; index: number } {
  if (source.kind === 'spine') return { siblings: arr, index: source.index };
  if (source.kind === 'spawnArm') return { siblings: arr[source.group]?.arm ?? [], index: source.arm };
  if (source.kind === 'switchArm')
    return { siblings: arr[source.group]?.switch?.branches[source.branch]?.arm ?? [], index: source.arm };
  if (source.kind === 'switchArmChild') {
    const container = arr[source.group]?.switch?.branches[source.branch]?.arm?.[source.slot];
    if (container?.type === 'spawn') return { siblings: container.arm ?? [], index: source.arm };
    if (container?.type === 'group') return { siblings: container.arms?.[source.arm] ?? [], index: 0 };
    return { siblings: [], index: 0 };
  }
  return { siblings: arr[source.group]?.arms?.[source.arm] ?? [], index: 0 }; // group arm
}

/**
 * The per-contact content-override key for the step a {@link DragSource} points at — the SAME key the
 * engine computes from an arm's `(path, cursor)` (see `sequenceOverrideKey`), so an edit here lands on the
 * key the send actually reads. A group arm's editable step is its first (cursor 0); spawn / switch carry
 * the arm cursor directly.
 */
function coordsToKey(source: DragSource): string {
  if (source.kind === 'spine') return sequenceOverrideKey([], source.index);
  if (source.kind === 'spawnArm') return sequenceOverrideKey([source.group, 'spawn'], source.arm);
  if (source.kind === 'switchArm') return sequenceOverrideKey([source.group, source.branch], source.arm);
  // Best-effort key for a nested container step (per-contact overrides don't reach this depth yet).
  if (source.kind === 'switchArmChild') return sequenceOverrideKey([source.group, source.branch, source.slot], source.arm);
  return sequenceOverrideKey([source.group, source.arm], 0); // group arm
}

/** Only steps whose SEND reads the override are per-contact editable (mirrors the engine's CONTENT_STEP_TYPES). */
function isContentEditable(step: SequenceStep): boolean {
  return step.type === 'message' || step.type === 'connect' || step.type === 'inmail' || step.type === 'email';
}

/**
 * The graphical sequence editor on a pannable / zoomable canvas: step nodes centered on a vertical
 * SVG spine, with on-canvas "+" insertion points. A node is dragged by a cursor-tracking ghost — drop
 * it in a gap to reorder, or onto another basic step to combine the two into a parallel group (wait /
 * parallel-container nodes can only reorder). Every structural edit (insert / reorder / merge / remove
 * / wait duration) autosaves via `sequence.update` — no save button. Owns its editing state (seeded
 * from props at mount) so the post-save `get` refetch doesn't clobber in-flight edits.
 */
export function SequenceGraphEditor({
  teamId,
  sequenceId,
  initialSteps,
  variables,
  documents,
  reminderActions,
  reminderActionsLoading,
  onCreateReminder,
  reminderCreating,
  readOnly = false,
  overrides,
  stepStatus,
  onSaveStepContent,
  onCompleteManualStep,
}: Props) {
  const utils = api.useUtils();
  const updateMutation = api.sequence.update.useMutation({
    onSuccess: () => {
      void utils.sequence.get.invalidate({ teamId, sequenceId });
      void utils.sequence.list.invalidate({ teamId });
    },
    onError: () => {
      void utils.sequence.get.invalidate({ teamId, sequenceId });
    },
  });

  const [steps, setSteps] = useState<SequenceStep[]>(initialSteps);
  const stepsRef = useRef<SequenceStep[]>(initialSteps);
  const [pickerAt, setPickerAt] = useState<number | null>(null);
  // Hovering the "Click + to add your first step" hint animates the (separate) leading "+" as if hovered.
  const [firstStepHintHovered, setFirstStepHintHovered] = useState(false);
  const [dragSource, setDragSource] = useState<DragMove | null>(null);
  const dragSourceRef = useRef<DragMove | null>(null);
  // A gesture starts on pointer-down but only becomes a real drag once it crosses DRAG_THRESHOLD; until
  // then it's a potential click. `armed` gates every drag visual (ghost, grabbing cursor, drop preview,
  // dimming) so a plain click never flickers a drag.
  const [dragArmed, setDragArmed] = useState(false);
  const dragArmedRef = useRef(false);
  // Where the dragged step will land — held in a ref (read by the drop handler) since the drop feedback is
  // painted imperatively (screen-space, so it works in ANY surface without in-canvas layout math).
  const dropRef = useRef<DropTarget | null>(null);
  const ghostRef = useRef<HTMLDivElement | null>(null);
  const pointerRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const dragStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  // The reorder insertion bar (a `fixed` element) + the element currently outlined as a merge target — both
  // positioned/styled imperatively in the drag move handler so they track any surface.
  const dropBarRef = useRef<HTMLDivElement | null>(null);
  const mergeElRef = useRef<HTMLElement | null>(null);
  // Which step's content editor is open — a spine step or a group arm (chip), or null.
  const [editing, setEditing] = useState<DragSource | null>(null);
  // The DOM element the content editor (a PORTALED Floating-UI popover) anchors to. Derived from `editing`
  // in the layout effect below — NOT captured from whatever was last clicked — so it's deterministic
  // (never null / stale / detached) and consistent: the same step opens the editor in the same place
  // whether you clicked its node, its content preview, or an arm chip.
  const [editorAnchor, setEditorAnchor] = useState<Element | null>(null);
  const editorFloat = useFloating({
    open: editing != null,
    placement: 'right-start',
    strategy: 'fixed',
    whileElementsMounted: (reference, floating, update) => autoUpdate(reference, floating, update, { animationFrame: true }),
    middleware: [offset(12), flip({ padding: 12 }), shift({ padding: 12 })],
    elements: { reference: editorAnchor },
  });
  // The step element most recently pointed at (any editable step carries `data-seq-step`) — used to anchor
  // the editor to the ACTUAL step, not its container. Written on pointerdown (before `editing` flips), read
  // by the layout effect below. A ref (not state) so it never triggers a render.
  const clickedStepElRef = useRef<Element | null>(null);
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      clickedStepElRef.current = e.target instanceof Element ? e.target.closest('[data-seq-step]') : null;
    };
    window.addEventListener('pointerdown', onDown, true);
    return () => window.removeEventListener('pointerdown', onDown, true);
  }, []);
  // A spine step anchors to its own node (`data-node-index`) — deterministic, always resolves. An ARM step
  // (a group arm / spawn arm / switch-branch step) does NOT: its container's spine node can sit far away —
  // a switch drops its branches well below the switch pill — so anchoring to `editing.group` floated the
  // editor way up at the container. Anchor to the actual clicked step element instead (falling back to the
  // container node if we somehow didn't capture one). useLayoutEffect so the anchor is in place before
  // paint — no first-frame flash at the viewport origin.
  useLayoutEffect(() => {
    if (editing == null) return;
    if (editing.kind === 'spine') {
      setEditorAnchor(document.querySelector(`[data-node-index="${editing.index}"]`));
      return;
    }
    setEditorAnchor(clickedStepElRef.current ?? document.querySelector(`[data-node-index="${editing.group}"]`));
  }, [editing]);
  // readOnly mode: the in-progress per-contact body/subject edit for the open step (null → show the saved
  // override or the template). Committed to `onSaveStepContent` on close; the template `steps` never change.
  const [draftOverride, setDraftOverride] = useState<{ body?: string; subject?: string } | null>(null);
  // The group whose inline add-step picker is open — its wrapper is raised so the picker (rendered
  // inside that wrapper's stacking context) isn't painted over by the insert slots / faces.
  const [groupPickerOpen, setGroupPickerOpen] = useState<number | null>(null);
  // Same, for a switch's route-branch pickers — raise the switch wrapper while any branch picker is open.
  const [switchPickerOpen, setSwitchPickerOpen] = useState<number | null>(null);
  // A container (parallel group / spawn) just inserted from the "+" picker auto-opens its add-step
  // picker, so you land ready to add the first step — like a drag-merge lands you with steps already in
  // — instead of on an empty box. Holds that container's index until the freshly-mounted node consumes
  // it (cleared one-shot by the effect below).
  const [autoOpenContainerAt, setAutoOpenContainerAt] = useState<number | null>(null);
  const [expandedPreStep, setExpandedPreStep] = useState<string | null>(null);
  // Open picker for a not-accepted arm insertion point: { step: main index, arm: arm insertion index }.
  const [armPickerAt, setArmPickerAt] = useState<{ step: number; arm: number } | null>(null);
  const isArmPicker = (step: number, arm: number) => armPickerAt?.step === step && armPickerAt.arm === arm;

  const commitLocal = (next: SequenceStep[]) => {
    stepsRef.current = next;
    setSteps(next);
  };
  const persistSteps = (next: SequenceStep[]) => {
    commitLocal(next);
    updateMutation.mutate({ teamId, sequenceId, steps: next });
  };

  // All structural edits read from stepsRef (the latest committed array) rather than the render-scope
  // `steps`, so an edit can never be computed from a stale snapshot mid drag-reorder.
  const insertAt = (index: number, type: SequenceStepType) => {
    // insertSpineStep applies creation defaults + auto-follow-ups (a connect trails an "Await accept").
    persistSteps(insertSpineStep(stepsRef.current, index, type));
    setPickerAt(null);
    // Steps you almost always write into open their editor immediately (see stepOpensEditorOnInsert).
    if (stepOpensEditorOnInsert(type)) setEditing({ kind: 'spine', index });
  };

  // Insert a parallel container — a `group` seeded with two empty arms, a detached `spawn` with one, or a
  // `switch`. It lands ready to fill in: a group/spawn auto-opens its add-step picker, while a switch
  // auto-opens its first branch's RANGE editor (the range is what matters first) — see autoOpenContainerAt.
  const insertContainerAt = (index: number, type: SequenceContainerStepType) => {
    const step: SequenceStep =
      type === 'group'
        ? { type: 'group', arms: [[], []], join: 'all' }
        : type === 'switch'
          ? newSwitchStep()
          : { type: 'spawn', arm: [] };
    const next = stepsRef.current.slice();
    next.splice(index, 0, step);
    setPickerAt(null);
    persistSteps(next);
    setAutoOpenContainerAt(index);
  };

  const remove = (index: number) => persistSteps(stepsRef.current.filter((_, i) => i !== index));

  const setWait = (index: number, waitDays: number, waitUnit: WaitUnit) => {
    persistSteps(stepsRef.current.map((s, i) => (i === index ? { ...s, waitDays, waitUnit } : s)));
  };
  const setDeadlineDays = (index: number, deadlineDays: number) => {
    persistSteps(stepsRef.current.map((s, i) => (i === index ? { ...s, deadlineDays } : s)));
  };

  // ── not-accepted arm (branches.notAccepted) ──
  const updateStepAt = (index: number, fn: (s: SequenceStep) => SequenceStep) =>
    persistSteps(stepsRef.current.map((s, i) => (i === index ? fn(s) : s)));
  // Create the arm, auto-seeded with a pinned Withdraw step.
  const addArm = (index: number) =>
    updateStepAt(index, (s) => ({ ...s, branches: { ...s.branches, notAccepted: [{ type: 'withdraw' }] } }));
  const removeArm = (index: number) => updateStepAt(index, (s) => ({ ...s, branches: undefined }));
  const insertArmStep = (index: number, armIndex: number, type: SequenceStepType) => {
    setArmPickerAt(null);
    // Never insert before the pinned Withdraw (arm index 0).
    const at = Math.max(1, armIndex);
    updateStepAt(index, (s) => {
      const arm = (s.branches?.notAccepted ?? []).slice();
      arm.splice(at, 0, newSequenceStep(type));
      return { ...s, branches: { ...s.branches, notAccepted: arm } };
    });
  };
  const removeArmStep = (index: number, armIndex: number) => {
    // The pinned Withdraw (arm index 0) is non-removable — clearing the whole branch is `removeArm`.
    if (armIndex <= 0) return;
    updateStepAt(index, (s) => ({
      ...s,
      branches: { ...s.branches, notAccepted: (s.branches?.notAccepted ?? []).filter((_, i) => i !== armIndex) },
    }));
  };
  const setArmWait = (index: number, armIndex: number, waitDays: number, waitUnit: WaitUnit) =>
    updateStepAt(index, (s) => ({
      ...s,
      branches: {
        ...s.branches,
        notAccepted: (s.branches?.notAccepted ?? []).map((a, i) => (i === armIndex ? { ...a, waitDays, waitUnit } : a)),
      },
    }));
  const setArmDeadlineDays = (index: number, armIndex: number, deadlineDays: number) =>
    updateStepAt(index, (s) => ({
      ...s,
      branches: {
        ...s.branches,
        notAccepted: (s.branches?.notAccepted ?? []).map((a, i) => (i === armIndex ? { ...a, deadlineDays } : a)),
      },
    }));

  // One-shot: the freshly-mounted container's own mount effect opens its picker (child effects run
  // before this parent effect), so clear the flag right after to avoid a stale index later pointing at
  // the wrong node once steps are reordered.
  useEffect(() => {
    if (autoOpenContainerAt != null) setAutoOpenContainerAt(null);
  }, [autoOpenContainerAt]);

  // Open a step's content editor. In readOnly (per-contact) mode only content SEND steps that aren't already
  // sent open — each open starts a fresh per-contact draft; the full editor opens any editable step.
  const openEditor = (source: DragSource) => {
    if (!readOnly) {
      setEditing(source);
      return;
    }
    const step = stepForSource(source, stepsRef.current);
    if (step == null || !isContentEditable(step)) return;
    if (stepStatus?.[coordsToKey(source)] === 'sent') return; // already delivered — locked
    setDraftOverride(null);
    setEditing(source);
  };

  // ── step content editor (body / subject / like-count) ──
  const editingStep = editing == null ? null : stepForSource(editing, steps);
  const editKey = editing == null ? null : coordsToKey(editing);
  // For an email step, the nearest earlier email's subject on its own path — drives the editor's
  // "continue previous thread" toggle (null → first email, no toggle, own subject required).
  const editingSiblings = editing == null ? null : siblingsForSource(editing, steps);
  const editingPrevEmailSubject =
    editingStep?.type === 'email' && editingSiblings != null
      ? previousEmailSubject(editingSiblings.siblings, editingSiblings.index)
      : null;
  // In readOnly mode the editor shows THIS contact's content — the in-progress draft, else the saved
  // override, else the template — while the template `steps` stay untouched.
  const editorStep =
    editingStep == null
      ? null
      : !readOnly
        ? editingStep
        : {
            ...editingStep,
            body: draftOverride?.body ?? (editKey != null ? overrides?.[editKey]?.body : undefined) ?? editingStep.body,
            subject: draftOverride?.subject ?? (editKey != null ? overrides?.[editKey]?.subject : undefined) ?? editingStep.subject,
          };
  // Live field edits update local state only (smooth typing); the change is persisted when the editor
  // closes. In readOnly mode the edit is a per-contact override tracked separately — the template never changes.
  const updateEditingStepLocal = (next: SequenceStep) => {
    if (editing == null) return;
    if (readOnly) {
      setDraftOverride({ body: next.body, subject: next.subject });
      return;
    }
    const cur = stepsRef.current;
    if (editing.kind === 'spine') commitLocal(cur.map((s, i) => (i === editing.index ? next : s)));
    else if (editing.kind === 'spawnArm')
      commitLocal(
        cur.map((s, i) =>
          i === editing.group ? { ...s, arm: (s.arm ?? []).map((a, k) => (k === editing.arm ? next : a)) } : s,
        ),
      );
    else if (editing.kind === 'switchArm')
      commitLocal(
        cur.map((s, i) => {
          if (i !== editing.group || s.switch == null) return s;
          const branches = s.switch.branches.map((b, bi) =>
            bi === editing.branch ? { ...b, arm: b.arm.map((a, k) => (k === editing.arm ? next : a)) } : b,
          );
          return { ...s, switch: { ...s.switch, branches } };
        }),
      );
    else if (editing.kind === 'switchArmChild')
      commitLocal(
        cur.map((s, i) => {
          if (i !== editing.group || s.switch == null) return s;
          const branches = s.switch.branches.map((b, bi) => {
            if (bi !== editing.branch) return b;
            const arm = b.arm.map((c, slot) => {
              if (slot !== editing.slot) return c;
              if (c.type === 'spawn') return { ...c, arm: (c.arm ?? []).map((a, k) => (k === editing.arm ? next : a)) };
              if (c.type === 'group') return { ...c, arms: (c.arms ?? []).map((a, k) => (k === editing.arm ? [next] : a)) };
              return c;
            });
            return { ...b, arm };
          });
          return { ...s, switch: { ...s.switch, branches } };
        }),
      );
    else
      commitLocal(
        cur.map((s, i) =>
          i === editing.group ? { ...s, arms: (s.arms ?? []).map((a, k) => (k === editing.arm ? [next] : a)) } : s,
        ),
      );
  };
  const closeEditor = useCallback(() => {
    if (readOnly) {
      // Persist the per-contact override (only if it changed) — never the template.
      if (draftOverride != null && onSaveStepContent != null && editing != null) {
        void onSaveStepContent(coordsToKey(editing), draftOverride);
      }
      setEditing(null);
      setDraftOverride(null);
      return;
    }
    updateMutation.mutate({ teamId, sequenceId, steps: stepsRef.current });
    setEditing(null);
  }, [readOnly, draftOverride, onSaveStepContent, editing, updateMutation, teamId, sequenceId]);

  // Persist the pending edit and close on Escape / a click outside the editor card.
  useEffect(() => {
    if (editing == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeEditor();
    };
    const onDown = (e: PointerEvent) => {
      // The mention dropdowns (@ variables / / documents / { comments) portal outside the card as
      // `.tippy-box` popups, and the reminder-action dropdown renders in a native `[popover]` top-layer —
      // a click in either is part of editing (picking a mention / a reminder), not a click-away, so keep
      // the editor open.
      if (
        e.target instanceof Element &&
        e.target.closest('[data-step-editor]') == null &&
        e.target.closest('.tippy-box') == null &&
        e.target.closest('[popover]') == null
      )
        closeEditor();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onDown, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDown, true);
    };
  }, [editing, closeEditor]);

  // Close the step picker (main spine or arm) on Escape or a click/pointer outside it.
  useEffect(() => {
    if (pickerAt == null && armPickerAt == null) return;
    const closeAll = () => {
      setPickerAt(null);
      setArmPickerAt(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAll();
    };
    const onDown = (e: PointerEvent) => {
      if (e.target instanceof Element && e.target.closest('[data-sequence-picker]') == null) closeAll();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onDown, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDown, true);
    };
  }, [pickerAt, armPickerAt]);

  // Close an expanded pre-step on Escape or a click/pointer outside it.
  useEffect(() => {
    if (expandedPreStep == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpandedPreStep(null);
    };
    const onDown = (e: PointerEvent) => {
      if (e.target instanceof Element && e.target.closest('[data-prestep]') == null) setExpandedPreStep(null);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onDown, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDown, true);
    };
  }, [expandedPreStep]);

  const beginDrag = (source: DragMove, e: { clientX: number; clientY: number }) => {
    dragSourceRef.current = source;
    pointerRef.current = { x: e.clientX, y: e.clientY };
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    dropRef.current = null;
    dragArmedRef.current = false;
    setDragArmed(false);
    setDragSource(source);
  };
  // Open the content editor for a line step at `addr` (spine / switch branch / spawn arm) — mirrors the
  // spine's click-to-edit so a branch step opens the SAME editor, anchored to itself.
  const openEditorForAddr = (addr: StepAddr) => {
    const step = stepAt(stepsRef.current, addr);
    if (step == null || !stepIsEditable(step)) return;
    if (addr.line.kind === 'spine') setEditing({ kind: 'spine', index: addr.index });
    else if (addr.line.kind === 'switchBranch')
      setEditing({ kind: 'switchArm', group: addr.line.group, branch: addr.line.branch, arm: addr.index });
    else setEditing({ kind: 'spawnArm', group: addr.line.group, arm: addr.index });
  };
  // Any LINE step (spine / switch branch / spawn arm) is a drag source: below the threshold it's a click
  // that opens the editor, past it a cross-surface move. Locked view: click-to-edit only (structure frozen).
  const startStepDrag = (addr: StepAddr, e: React.PointerEvent) => {
    // Let the delete button / wait input handle their own pointer events.
    if (e.target instanceof Element && e.target.closest('button, input, textarea, select, a')) return;
    // Stop here so a branch step's gesture never bubbles up to its container node's own drag/click.
    e.stopPropagation();
    if (readOnly) {
      openEditorForAddr(addr);
      return;
    }
    beginDrag({ kind: 'lineStep', addr }, e);
  };
  const startDrag = (e: React.PointerEvent, index: number) => startStepDrag({ line: { kind: 'spine' }, index }, e);
  // Pull an arm (chip) out of the parallel group at `group`; a drop on the spine turns it into a step.
  const startArmDrag = (group: number, arm: number, e: React.PointerEvent) => beginDrag({ kind: 'arm', group, arm }, e);
  // Clear the imperative drop feedback (insertion bar + merge outline).
  const clearDropFeedback = () => {
    if (dropBarRef.current != null) dropBarRef.current.style.display = 'none';
    if (mergeElRef.current != null) {
      mergeElRef.current.style.outline = '';
      mergeElRef.current.style.outlineOffset = '';
      mergeElRef.current = null;
    }
  };

  // Drag: a ghost tracks the cursor (screen-space); the target is computed on move by hit-testing EVERY
  // line step (spine + branches carry `data-line`/`data-idx`), so a step reorders within its line, moves to
  // another line (spine ↔ switch branch ↔ spawn arm), or merges onto a node to form/join a parallel group.
  // An arm chip pulled out of a group still lands on the spine. Nothing mutates `steps` mid-drag — the move
  // (sequenceMove.moveStep) is applied once on drop; Escape cancels. Drop feedback is painted imperatively.
  const dragActive = dragSource !== null;
  useEffect(() => {
    if (!dragActive) return;
    const onMove = (e: PointerEvent) => {
      const src = dragSourceRef.current;
      if (src == null) return;
      pointerRef.current = { x: e.clientX, y: e.clientY };
      // Below the threshold the gesture is still a potential click — don't arm the drag yet.
      if (!dragArmedRef.current) {
        const moved = Math.hypot(e.clientX - dragStartRef.current.x, e.clientY - dragStartRef.current.y);
        if (moved < DRAG_THRESHOLD) return;
        dragArmedRef.current = true;
        setDragArmed(true);
      }
      if (ghostRef.current != null)
        ghostRef.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px) translate(-50%, -50%)`;

      // Every line step on the canvas, with its address + screen rect.
      const els = Array.from(document.querySelectorAll<HTMLElement>('[data-line]'))
        .map((el) => {
          const line = parseLine(el.getAttribute('data-line'));
          const index = Number(el.getAttribute('data-idx'));
          return line != null && Number.isFinite(index) ? { line, index, r: el.getBoundingClientRect(), el } : null;
        })
        .filter((x): x is { line: LineAddr; index: number; r: DOMRect; el: HTMLElement } => x != null);

      const dragged =
        src.kind === 'lineStep'
          ? stepAt(stepsRef.current, src.addr)
          : (stepsRef.current[src.group]?.arms?.[src.arm]?.[0] ?? null);
      const isSelf = (a: { line: LineAddr; index: number }) =>
        src.kind === 'lineStep' && sameLine(a.line, src.addr.line) && a.index === src.addr.index;
      // A group-arm extract can only land back on the spine (extractArmToSpine / mergeArmIntoNode).
      const targets = src.kind === 'arm' ? els.filter((t) => t.line.kind === 'spine') : els;

      let nextDrop: DropTarget | null = null;
      // Merge: hovering a node that can receive the (basic) dragged step.
      if (canMergeIntoGroup(dragged)) {
        const over = targets.find(
          (t) => !isSelf(t) && e.clientX >= t.r.left && e.clientX <= t.r.right && e.clientY >= t.r.top && e.clientY <= t.r.bottom,
        );
        if (over != null && canReceiveMerge(stepAt(stepsRef.current, { line: over.line, index: over.index })))
          nextDrop = { kind: 'merge', onto: { line: over.line, index: over.index } };
      }
      // Reorder: pick the target LINE by cursor X (its column), the gap by cursor Y within it.
      let barRect: { top: number; left: number; width: number } | null = null;
      if (nextDrop == null && targets.length > 0) {
        const byLine = new Map<string, { line: LineAddr; rects: DOMRect[] }>();
        for (const t of targets) {
          const key = serializeLine(t.line);
          const g = byLine.get(key) ?? { line: t.line, rects: [] };
          g.rects.push(t.r);
          byLine.set(key, g);
        }
        // Only a line the cursor is genuinely OVER (its column AND vertical band, ± a small margin) is a
        // candidate — so a drag that wanders into empty space (e.g. below a switch, over an empty branch it
        // can't reach) targets NOTHING and cancels on release, instead of silently snapping to a far line.
        const NEAR = 44;
        let best: { line: LineAddr; rects: DOMRect[] } | null = null;
        let bestDist = Infinity;
        for (const g of byLine.values()) {
          const left = Math.min(...g.rects.map((r) => r.left));
          const right = Math.max(...g.rects.map((r) => r.right));
          const topY = Math.min(...g.rects.map((r) => r.top));
          const botY = Math.max(...g.rects.map((r) => r.bottom));
          if (e.clientX < left - NEAR || e.clientX > right + NEAR || e.clientY < topY - NEAR || e.clientY > botY + NEAR) continue;
          const dist = e.clientX >= left && e.clientX <= right ? 0 : Math.min(Math.abs(e.clientX - left), Math.abs(e.clientX - right));
          if (dist < bestDist) {
            bestDist = dist;
            best = g;
          }
        }
        if (best != null) {
          const sorted = best.rects.slice().sort((a, b) => a.top - b.top);
          let gap = 0;
          for (const r of sorted) if (e.clientY > r.top + r.height / 2) gap++;
          nextDrop = { kind: 'reorder', line: best.line, gap };
          const first = sorted[0]!;
          const last = sorted[sorted.length - 1]!;
          const top = gap === 0 ? first.top - 5 : gap >= sorted.length ? last.bottom + 5 : (sorted[gap - 1]!.bottom + sorted[gap]!.top) / 2;
          barRect = { top, left: first.left, width: Math.max(...sorted.map((r) => r.width)) };
        }
      }
      // Skip an invalid / no-op line-step drop so we never preview one.
      if (nextDrop != null && src.kind === 'lineStep' && !canDrop(stepsRef.current, src.addr, nextDrop)) {
        nextDrop = null;
        barRect = null;
      }
      dropRef.current = nextDrop;

      // Imperative feedback: outline a merge target, else show the reorder insertion bar (screen-space).
      const overEl =
        nextDrop?.kind === 'merge'
          ? targets.find((t) => sameLine(t.line, nextDrop.onto.line) && t.index === nextDrop.onto.index)?.el ?? null
          : null;
      if (mergeElRef.current != null && mergeElRef.current !== overEl) {
        mergeElRef.current.style.outline = '';
        mergeElRef.current.style.outlineOffset = '';
        mergeElRef.current = null;
      }
      if (overEl != null) {
        overEl.style.outline = '2px solid rgb(139 92 246)'; // violet-500
        overEl.style.outlineOffset = '3px';
        mergeElRef.current = overEl;
      }
      if (dropBarRef.current != null) {
        if (barRect != null) {
          dropBarRef.current.style.display = 'block';
          dropBarRef.current.style.top = `${barRect.top}px`;
          dropBarRef.current.style.left = `${barRect.left}px`;
          dropBarRef.current.style.width = `${barRect.width}px`;
        } else {
          dropBarRef.current.style.display = 'none';
        }
      }
    };
    const onUp = () => {
      const src = dragSourceRef.current;
      const target = dropRef.current;
      if (dragArmedRef.current) {
        if (src != null && target != null) {
          let next: SequenceStep[] | null = null;
          if (src.kind === 'lineStep') next = moveStep(stepsRef.current, src.addr, target);
          else if (target.kind === 'merge' && target.onto.line.kind === 'spine')
            next = mergeArmIntoNode(stepsRef.current, src.group, src.arm, target.onto.index);
          else if (target.kind === 'reorder' && target.line.kind === 'spine')
            next = extractArmToSpine(stepsRef.current, src.group, src.arm, target.gap);
          // Skip a no-op drop (helper returns the same array): no local churn, no wasted save.
          if (next != null && next !== stepsRef.current) {
            commitLocal(next);
            updateMutation.mutate({ teamId, sequenceId, steps: next });
          }
        }
      } else if (src != null) {
        // A click (never crossed the threshold) opens the step's editor — a line step (spine / branch) or a
        // parallel-group arm chip, if the step has content to edit.
        if (src.kind === 'lineStep') openEditorForAddr(src.addr);
        else {
          const step = stepsRef.current[src.group]?.arms?.[src.arm]?.[0];
          if (step != null && stepIsEditable(step)) setEditing({ kind: 'arm', group: src.group, arm: src.arm });
        }
      }
      cancelDrag();
    };
    const cancelDrag = () => {
      clearDropFeedback();
      dragSourceRef.current = null;
      dropRef.current = null;
      dragArmedRef.current = false;
      setDragArmed(false);
      setDragSource(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancelDrag();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('keydown', onKey);
    };
  }, [dragActive, teamId, sequenceId, updateMutation]);

  // Faces per node — how many enrolled people sit on each graph node + a small avatar sample. Polls so
  // the piles follow contacts as they advance through the sequence (shared with the full journey graph).
  const facesQuery = useSequenceFaces(teamId, sequenceId, !readOnly);
  // Memoized (like the full journey graph) so the `?? {}` fallback doesn't mint a fresh object each
  // render — a live poll that returns unchanged data then can't churn the canvas.
  const byNode = useMemo(() => facesQuery.data?.byNode ?? {}, [facesQuery.data]);
  const facesFor = (nodeIds: string[]): { total: number; sample: SequenceFace[] } => {
    let total = 0;
    const sample: SequenceFace[] = [];
    for (const id of nodeIds) {
      const f = byNode[id];
      if (f == null) continue;
      total += f.count;
      for (const s of f.sample) if (sample.length < 6) sample.push(s);
    }
    return { total, sample };
  };

  // Per-face actions (open detail in place / restart / remove / mark a call·reminder to-do done) — shared
  // with the full journey graph.
  const {
    onOpenContact: onOpenContactFace,
    onRestart: onRestartFace,
    onRemove: onRemoveFace,
    onCompleteTask: onCompleteTaskFace,
  } = useSequenceFaceActions(teamId, sequenceId);

  // Every journey node the authored graph already pins a face onto: the pre-step folds plus each present
  // step's own node + its park/wait lanes (recursively — not-accepted arms, parallel + spawned + switch
  // branches). Anyone on a node OUTSIDE this set is at risk of being silently dropped from the canvas.
  const claimedNodeIds = useMemo(() => {
    const ids = new Set<string>();
    for (const group of Object.values(PRE_STEP_NODE_IDS)) for (const id of group) ids.add(id);
    const walk = (list: SequenceStep[]) => {
      for (const s of list) {
        for (const id of stepFaceNodeIds(s.type)) ids.add(id);
        if (s.branches?.notAccepted != null) walk(s.branches.notAccepted);
        if (s.arms != null) for (const a of s.arms) walk(a);
        if (s.arm != null) walk(s.arm);
        if (s.switch != null) for (const b of s.switch.branches) walk(b.arm);
      }
    };
    walk(steps);
    return ids;
  }, [steps]);

  // Safety net (mirrors the full journey graph's "Other states" pile): anyone resting on a node the
  // authored graph doesn't claim — a shared park lane (off-hours wait, reconnect LinkedIn, needs
  // attention, …) or an unmodelled/renamed node — folds into ONE visible pile so no enrolled contact is
  // ever silently hidden. Terminal SUCCESS states (finished / responded) are skipped so a long-running
  // sequence's completed contacts don't clutter it — those are an outcome, not an open task.
  const offGraph = useMemo(() => {
    const orphans = Object.entries(byNode).filter(
      ([nodeId]) => !claimedNodeIds.has(nodeId) && !isJourneyDoneNode(nodeId),
    );
    const total = orphans.reduce((sum, [, f]) => sum + f.count, 0);
    const sample = orphans.flatMap(([, f]) => f.sample).slice(0, 6);
    return { total, sample };
  }, [byNode, claimedNodeIds]);

  const n = steps.length;
  // Branch layout (pure + recomputed each render, so it adapts as steps change): each side branch — a
  // spawn's "runs alongside" box or an await's not-accepted arm — gets its OWN vertical lane to the right
  // of the WIDEST node, so nothing overlaps whatever the node widths are. See {@link computeBranchLanes}.
  const lanes = computeBranchLanes(steps, CX);
  const branchXFor = lanes.branchXFor;
  // The not-accepted arm lives in the step's branch lane, nudged right (NOT_ACCEPTED_ARM_INSET) so its wide
  // "IF NOT ACCEPTED" edge label fits on the connector between the pills.
  const armXFor = (i: number) => lanes.branchXFor(i) + NOT_ACCEPTED_ARM_INSET;
  // The connector leaves each node at its actual right edge (a wide pill's edge, not a fixed offset).
  const nodeRightXFor = (i: number) => CX + spineNodeHalfWidth(steps[i] ?? { type: 'follow' });

  let maxBottom = slotCenterY(n) + STRIDE;
  let maxRight = lanes.contentRight(CX);
  steps.forEach((s, i) => {
    if (s.type === 'await_connect') {
      const armLen = s.branches?.notAccepted?.length ?? 0;
      if (armLen > 0) maxBottom = Math.max(maxBottom, nodeCenterY(i) + armLen * STRIDE);
    }
    // The spawn box is a full vertical sub-line centered on the node — reserve ~half its height below.
    if (s.type === 'spawn') {
      const armLen = s.arm?.length ?? 0;
      maxBottom = Math.max(maxBottom, nodeCenterY(i) + 60 + armLen * 44);
    }
    // The switch fan hangs BELOW the node, centered on the spine (column 0) and fanning RIGHT — reserve
    // the fan's width (from the spine) and the deepest branch's depth.
    if (s.type === 'switch') {
      // A branch's vertical extent is its own steps PLUS the tallest spawn arm flaired off it (which drops
      // down alongside the branch line) — so a fit / canvas reservation doesn't clip a long parallel arm.
      const deepest = (s.switch?.branches ?? []).reduce((m, b) => {
        const tallestSpawn = b.arm.reduce((mm, st) => Math.max(mm, st.type === 'spawn' ? (st.arm?.length ?? 0) : 0), 0);
        return Math.max(m, b.arm.length + tallestSpawn);
      }, 0);
      maxRight = Math.max(maxRight, switchFanLeft(CX) + switchFanWidth(s) + 40);
      maxBottom = Math.max(maxBottom, nodeCenterY(i) + SWITCH_HEADER + deepest * SWITCH_ROW + 60);
    }
  });
  // Content previews sit in the right gutter, each hugging its own node's right edge, capped so they
  // never poke into the branch lanes. Reserve room so the overflow-hidden canvas can't clip them.
  const previewLeftFor = (i: number) => nodeRightXFor(i) + PREVIEW_GAP;
  const previewMaxWidthFor = (i: number) =>
    lanes.laneCount > 0 ? Math.max(120, lanes.laneX0 - previewLeftFor(i) - 16) : PREVIEW_W;
  steps.forEach((s, i) => {
    if (stepHasContentPreview(s)) maxRight = Math.max(maxRight, previewLeftFor(i) + previewMaxWidthFor(i) + 24);
  });

  // The content editor is a portaled popover anchored (Floating UI) to the clicked node's element — no
  // in-canvas coordinate math, no reserved space; it can't be clipped and doesn't scale with zoom.
  // The step pickers are portaled to the body and floated by Floating UI (see SequenceGraphInsert), so
  // they no longer need reserved canvas space to avoid the overflow-hidden clip.
  const contentWidth = maxRight;
  const contentHeight = maxBottom;
  const expandedIndex = SEQUENCE_PRE_STEPS.findIndex((s) => s.id === expandedPreStep);
  const anyPickerOpen = pickerAt !== null || armPickerAt !== null;
  // Only reflect drag state once the gesture is armed — an un-armed gesture is still a pending click. Dims
  // the dragged SPINE node (branch steps dim via their own surface; the ghost is the main cue).
  const dragSpineIndex =
    dragArmed && dragSource?.kind === 'lineStep' && dragSource.addr.line.kind === 'spine' ? dragSource.addr.index : null;
  const draggedStep =
    dragSource == null
      ? null
      : dragSource.kind === 'lineStep'
        ? stepAt(steps, dragSource.addr)
        : (steps[dragSource.group]?.arms?.[dragSource.arm]?.[0] ?? null);

  return (
    <div className={cn('relative size-full', dragArmed && 'cursor-grabbing')}>
      <SequencePanZoom>
        {/* While dragging, kill pointer events on the canvas so passing over nodes doesn't fire their
            hover affordances (remove buttons, tooltips, the growing "+") and obscure the drop. The drop
            hit-test reads getBoundingClientRect + window listeners, so it's unaffected. */}
        <div
          className={cn('relative', dragArmed && 'cursor-grabbing [&_*]:pointer-events-none')}
          style={{ width: contentWidth, height: contentHeight }}>
          <svg width={contentWidth} height={contentHeight} className="absolute left-0 top-0 overflow-visible">
            {/* Dashed through the automatic front-half, solid through the editable steps. */}
            <line
              x1={CX}
              y1={preCenterY(0)}
              x2={CX}
              y2={slotCenterY(0)}
              className="stroke-lynch-300 dark:stroke-slate-600"
              strokeWidth={2}
              strokeDasharray="4 5"
            />
            <line
              x1={CX}
              y1={slotCenterY(0)}
              x2={CX}
              y2={slotCenterY(n)}
              className="stroke-lynch-300 dark:stroke-slate-600"
              strokeWidth={2}
              strokeLinecap="round"
            />
            {expandedIndex >= 0 && (
              <line
                x1={CX + 58}
                y1={preCenterY(expandedIndex)}
                x2={CX + 96}
                y2={preCenterY(expandedIndex)}
                className="stroke-blue-400"
                strokeWidth={2}
              />
            )}
            {/* not-accepted arm connectors (rose = the negative branch) */}
            {steps.map((step, i) => {
              const arm = step.branches?.notAccepted;
              if (step.type !== 'await_connect' || arm == null || arm.length === 0) return null;
              const y0 = nodeCenterY(i);
              const ax = armXFor(i);
              return (
                <Fragment key={`arm-line-${i}`}>
                  {/* Start at the node CENTER, not the estimated right edge: the opaque pill paints over the
                      SVG, so the line stays hidden under the pill and emerges exactly at its real right edge —
                      always connected, regardless of how wide the "Await accept · up to N days" pill renders. */}
                  <path
                    d={`M ${CX} ${y0} H ${ax}`}
                    className="stroke-rose-300 dark:stroke-rose-500/60"
                    strokeWidth={2}
                    fill="none"
                  />
                  <line
                    x1={ax}
                    y1={y0}
                    x2={ax}
                    y2={y0 + (arm.length - 1) * STRIDE + STRIDE / 2}
                    className="stroke-rose-300 dark:stroke-rose-500/60"
                    strokeWidth={2}
                    strokeLinecap="round"
                  />
                </Fragment>
              );
            })}
            {/* spawn side-branch: a connector from the node's right edge across to its "runs alongside"
                lane. (A switch draws its own tree DOWN into the fan — see SequenceSwitchBranches.) */}
            {steps.map((step, i) => {
              if (step.type !== 'spawn') return null;
              const y0 = nodeCenterY(i);
              return (
                <line
                  key={`branch-line-${i}`}
                  x1={nodeRightXFor(i)}
                  y1={y0}
                  x2={branchXFor(i)}
                  y2={y0}
                  className="stroke-lynch-300 dark:stroke-slate-600"
                  strokeWidth={2}
                  strokeLinecap="round"
                />
              );
            })}
          </svg>

          <div
            className="absolute whitespace-nowrap text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500"
            style={{ left: CX, top: PRE_TOP - STRIDE / 2, transform: 'translate(-50%, -50%)' }}>
            Automatic · research &amp; qualify
          </div>

          {SEQUENCE_PRE_STEPS.map((preStep, p) => {
            const isOpen = expandedPreStep === preStep.id;
            return (
              <Fragment key={preStep.id}>
                <div
                  data-no-pan
                  data-prestep
                  className={cn('absolute', isOpen ? 'z-40' : 'z-10')}
                  style={{ left: CX, top: preCenterY(p), transform: 'translate(-50%, -50%)' }}>
                  <SequencePreStepNode
                    preStep={preStep}
                    expanded={isOpen}
                    onToggle={() => setExpandedPreStep((cur) => (cur === preStep.id ? null : preStep.id))}
                  />
                </div>
                {isOpen && (
                  <div
                    data-no-pan
                    data-prestep
                    // Top-anchored (opens downward) so a tall panel never runs off the top of the
                    // overflow-hidden canvas; -20px roughly aligns its header with the node.
                    className="absolute z-40"
                    style={{ left: CX + 96, top: preCenterY(p), transform: 'translate(0, -20px)' }}>
                    <SequencePreStepDetail preStep={preStep} />
                  </div>
                )}
              </Fragment>
            );
          })}

          {!readOnly &&
            Array.from({ length: n + 1 }).map((_, k) => {
            // A Switch ends the main line — its branches are terminal (no rejoin), so nothing can run after
            // it on the spine. Suppress the insert that would sit below a switch instead of offering an
            // "add a step" that only gets flagged invalid (see sequenceStepIssue).
            if (k > 0 && steps[k - 1]?.type === 'switch') return null;
            return (
              // While a picker is open, other "+" tiles stay visible but inert (no layout jump). The "+"
              // stays centered on the spine; its menu is portaled + floated (SequenceGraphInsert).
              <div
                key={`slot-${k}`}
                data-no-pan
                className={cn('absolute', pickerAt === k ? 'z-50' : 'z-20')}
                style={{ left: CX, top: slotCenterY(k), transform: 'translate(-50%, -50%)' }}>
                <SequenceGraphInsert
                  active={pickerAt === k}
                  inert={anyPickerOpen && pickerAt !== k}
                  hovered={k === 0 && n === 0 && firstStepHintHovered}
                  onOpen={() => setPickerAt(k)}
                  onPick={(type) => insertAt(k, type)}
                  onPickContainer={(type) => insertContainerAt(k, type)}
                  onCancel={() => setPickerAt(null)}
                  disabledReason={(type) => insertDisabledReason(steps, k, type)}
                />
              </div>
            );
            })}

          {steps.map((step, i) => {
            // Locked view: mark this contact's progress — dim a spine step already sent (also non-clickable
            // in openEditor), and ring the one they're currently on. Nested-arm status is marked on its own node.
            const spineStatus = readOnly ? stepStatus?.[sequenceOverrideKey([], i)] : undefined;
            // Locked view: a call/reminder step the contact is currently parked on gets a "Mark done" control
            // that completes its to-do and advances the arm (via the step's manual-task wait node id).
            const manualWaitNode = spineStatus === 'current' ? manualTaskWaitNodeForStepType(step.type) : null;
            return (
            <div
              key={`node-${i}`}
              data-no-pan
              data-node-index={i}
              data-line="spine"
              data-idx={i}
              onPointerDown={(e) => startDrag(e, i)}
              className={cn(
                'absolute select-none',
                dragSpineIndex === i
                  ? 'z-40 pointer-events-none opacity-30'
                  : groupPickerOpen === i
                    ? 'z-50'
                    : 'z-10',
                spineStatus === 'sent' && 'opacity-60',
                spineStatus === 'current' && 'rounded-xl ring-2 ring-blue-400 ring-offset-2 ring-offset-slate-50 dark:ring-offset-neutral-900',
              )}
              style={{ left: CX, top: nodeCenterY(i), transform: 'translate(-50%, -50%)' }}>
              {/* A parallel group IS its button group — rendered inline on the spine (no separate icon /
                  caption / side box). Dragging the box background reorders the whole group; dragging a
                  chip pulls it out. */}
              {step.type === 'group' && (
                <SequenceParallelArms
                  inline
                  step={step}
                  onChange={(next) => updateStepAt(i, () => next)}
                  onRemove={readOnly ? undefined : () => remove(i)}
                  onDragOutArm={readOnly ? undefined : (arm, e) => startArmDrag(i, arm, e)}
                  draggingArm={dragArmed && dragSource?.kind === 'arm' && dragSource.group === i ? dragSource.arm : null}
                  onEditArm={(arm) => openEditor({ kind: 'arm', group: i, arm })}
                  onPickerOpenChange={(open) => setGroupPickerOpen(open ? i : null)}
                  autoOpenPicker={autoOpenContainerAt === i}
                  readOnly={readOnly}
                />
              )}
              {/* A switch IS its attribute pill on the spine (Age / ~AUM toggle); its route branches fan
                  out below (rendered separately). Dragging the pill background reorders the node. */}
              {step.type === 'switch' && (
                <SequenceSwitchNode
                  step={step}
                  invalid={sequenceStepIssue(steps, i)}
                  onChangeAttribute={(attribute) =>
                    updateStepAt(i, (s) => (s.switch == null ? s : { ...s, switch: { ...s.switch, attribute } }))
                  }
                  onRemove={() => remove(i)}
                  readOnly={readOnly}
                />
              )}
              {step.type !== 'group' && step.type !== 'switch' && (
                <SequenceGraphNode
                  step={step}
                  dragging={false}
                  invalid={sequenceStepIssue(steps, i)}
                  onRemove={() => remove(i)}
                  onChangeWait={(days, unit) => setWait(i, days, unit)}
                  onChangeDeadlineDays={(days) => setDeadlineDays(i, days)}
                  readOnly={readOnly}
                  onMarkDone={
                    manualWaitNode != null && onCompleteManualStep != null
                      ? () => onCompleteManualStep(manualWaitNode)
                      : undefined
                  }
                />
              )}
            </div>
            );
          })}

          {/* Content preview — a step's authored subject/body shown to the RIGHT of its node (the editor
              opens over this same column when you click it). Only content steps with text get a card. */}
          {steps.map((step, i) => {
            // Locked view: preview THIS contact's content — the saved override overlaid on the template.
            const previewStep =
              readOnly && overrides != null
                ? {
                    ...step,
                    body: overrides[sequenceOverrideKey([], i)]?.body ?? step.body,
                    subject: overrides[sequenceOverrideKey([], i)]?.subject ?? step.subject,
                  }
                : step;
            if (!stepHasContentPreview(previewStep)) return null;
            // Locked view: the preview is a click-to-edit target only when the step is actually editable —
            // a content send step that hasn't been sent yet. Otherwise it renders as static text (no dead
            // "Edit step content" click). The full editor always allows editing.
            const previewEditable =
              !readOnly || (stepStatus?.[sequenceOverrideKey([], i)] !== 'sent' && isContentEditable(previewStep));
            return (
              <div
                key={`preview-${i}`}
                data-no-pan
                className={cn(
                  'absolute z-10',
                  dragSpineIndex === i && 'pointer-events-none opacity-30',
                  readOnly && stepStatus?.[sequenceOverrideKey([], i)] === 'sent' && 'opacity-60',
                )}
                style={{ left: previewLeftFor(i), top: nodeCenterY(i), transform: 'translate(0, -50%)' }}>
                <SequenceStepPreview
                  step={previewStep}
                  maxWidthPx={previewMaxWidthFor(i)}
                  onEdit={previewEditable ? () => openEditor({ kind: 'spine', index: i }) : undefined}
                />
              </div>
            );
          })}

          {/* Faces — the people currently sitting on each node — in a gutter to the left of the spine. */}
          {SEQUENCE_PRE_STEPS.map((preStep, p) => {
            const f = facesFor(PRE_STEP_NODE_IDS[preStep.id] ?? []);
            if (f.total === 0) return null;
            return (
              <div
                key={`faces-pre-${preStep.id}`}
                className="absolute z-20"
                style={{ left: CX - 66, top: preCenterY(p), transform: 'translate(-100%, -50%)' }}>
                <SequenceNodeFaces
                  sample={f.sample}
                  total={f.total}
                  onOpenContact={onOpenContactFace}
                  onRestart={onRestartFace}
                  onRemove={onRemoveFace}
                  onCompleteTask={onCompleteTaskFace}
                />
              </div>
            );
          })}
          {steps.map((step, i) => {
            const f = facesFor(stepFaceNodeIds(step.type));
            if (f.total === 0) return null;
            return (
              <div
                key={`faces-node-${i}`}
                className="absolute z-20"
                style={{ left: CX - 66, top: nodeCenterY(i), transform: 'translate(-100%, -50%)' }}>
                <SequenceNodeFaces
                  sample={f.sample}
                  total={f.total}
                  onOpenContact={onOpenContactFace}
                  onRestart={onRestartFace}
                  onRemove={onRemoveFace}
                  onCompleteTask={onCompleteTaskFace}
                />
              </div>
            );
          })}

          {/* Not-accepted arms off await_connect steps (Withdraw → …). */}
          {steps.map((step, i) => {
            if (step.type !== 'await_connect') return null;
            const arm = step.branches?.notAccepted ?? null;
            const y0 = nodeCenterY(i);
            const ax = armXFor(i);

            if (arm == null) {
              // Locked view: no "if not accepted" branch authored — nothing to show (can't add one here).
              if (readOnly) return null;
              return (
                <div
                  key={`arm-stub-${i}`}
                  data-no-pan
                  className="absolute z-10"
                  // Sits just past the await pill's right edge (whatever its width) — clear of the node.
                  style={{ left: lanes.stubXFor(i), top: y0, transform: 'translate(0, -50%)' }}>
                  <Button size="2xs" color="red" tone="soft" icon={<General.Plus />} onClick={() => addArm(i)}>
                    if not accepted
                  </Button>
                </div>
              );
            }

            return (
              <Fragment key={`arm-${i}`}>
                {/* Edge label centered ON the connector, in the clear span between the await pill's right
                    edge and the Withdraw node (its bg occludes the rose line under it). The arm is inset
                    (NOT_ACCEPTED_ARM_INSET) so this wide label fits between the pills without overlapping. */}
                <div
                  data-no-pan
                  className="absolute z-10 flex items-center gap-1 rounded-full bg-slate-50 px-2 dark:bg-neutral-900"
                  style={{ left: (nodeRightXFor(i) + ax - WITHDRAW_HALF_W) / 2, top: y0, transform: 'translate(-50%, -50%)' }}>
                  <span className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-wide text-rose-400">
                    if not accepted
                  </span>
                  {!readOnly && (
                    <Button size="2xs" text icon={<General.XMark />} tooltip="Remove branch" onClick={() => removeArm(i)} />
                  )}
                </div>

                {arm.map((armStep, p) => {
                  const armY = y0 + p * STRIDE;
                  const armFaces = facesFor(stepFaceNodeIds(armStep.type));
                  return (
                    <Fragment key={`arm-${i}-node-${p}`}>
                      <div
                        data-no-pan
                        className="absolute z-10"
                        style={{ left: ax, top: armY, transform: 'translate(-50%, -50%)' }}>
                        {p === 0 && (
                          <span className="flex h-9 cursor-default items-center gap-1.5 rounded-full border border-rose-200 bg-white px-3 text-xs font-medium text-rose-500 shadow-sm dark:border-rose-500/40 dark:bg-slate-900 dark:text-rose-300 [&>svg]:size-4">
                            {STEP_META.withdraw.icon}
                            Withdraw invite
                          </span>
                        )}
                        {p > 0 && (
                          <SequenceGraphNode
                            step={armStep}
                            dragging={false}
                            invalid={sequenceStepIssue(arm, p)}
                            onRemove={readOnly ? undefined : () => removeArmStep(i, p)}
                            onChangeWait={readOnly ? undefined : (days, unit) => setArmWait(i, p, days, unit)}
                            onChangeDeadlineDays={readOnly ? undefined : (days) => setArmDeadlineDays(i, p, days)}
                            readOnly={readOnly}
                          />
                        )}
                      </div>

                      {armFaces.total > 0 && (
                        <div
                          className="absolute z-20"
                          style={{ left: ax - 66, top: armY, transform: 'translate(-100%, -50%)' }}>
                          <SequenceNodeFaces
                            sample={armFaces.sample}
                            total={armFaces.total}
                            onOpenContact={onOpenContactFace}
                            onRestart={onRestartFace}
                            onRemove={onRemoveFace}
                            onCompleteTask={onCompleteTaskFace}
                          />
                        </div>
                      )}

                      {!readOnly && (
                        <div
                          data-no-pan
                          className={cn('absolute', isArmPicker(i, p + 1) ? 'z-50' : 'z-20')}
                          style={{ left: ax, top: armY + STRIDE / 2, transform: 'translate(-50%, -50%)' }}>
                          <SequenceGraphInsert
                            active={isArmPicker(i, p + 1)}
                            inert={anyPickerOpen && !isArmPicker(i, p + 1)}
                            onOpen={() => setArmPickerAt({ step: i, arm: p + 1 })}
                            onPick={(type) => insertArmStep(i, p + 1, type)}
                            onCancel={() => setArmPickerAt(null)}
                            disabledReason={(type) => insertDisabledReason(arm, p + 1, type)}
                          />
                        </div>
                      )}
                    </Fragment>
                  );
                })}
              </Fragment>
            );
          })}

          {/* A spawn is a side branch ("runs alongside"), so its box is drawn to the RIGHT of the node.
              (A group renders inline on the spine — see the node map above.) */}
          {steps.map((step, i) => {
            if (step.type !== 'spawn') return null;
            return (
              <div
                key={`parallel-${i}`}
                data-no-pan
                className={cn('absolute z-10', dragSpineIndex === i && 'pointer-events-none opacity-30')}
                // TOP-aligned + centered on its lane x: the branch's line starts exactly at its node's row
                // (where the horizontal connector meets it) and grows DOWN — never up into an earlier row —
                // so an earlier branch's connector crosses this lane only where it's still empty.
                style={{ left: branchXFor(i), top: nodeCenterY(i), transform: 'translate(-50%, 0)' }}>
                <SequenceParallelArms
                  step={step}
                  spawnLineAddr={{ kind: 'spawnArm', group: i }}
                  onStepDrag={readOnly ? undefined : startStepDrag}
                  onChange={(next) => updateStepAt(i, () => next)}
                  onRemove={readOnly ? undefined : () => remove(i)}
                  onEditArm={(arm) => openEditor({ kind: 'spawnArm', group: i, arm })}
                  autoOpenPicker={autoOpenContainerAt === i}
                  readOnly={readOnly}
                />
              </div>
            );
          })}

          {/* A switch fans its route branches DOWN below the node (each branch its own terminal path).
              The fan is anchored so column 0 sits ON the spine (left = CX − half a column) and starts
              just below the pill; its own SVG draws the tree from the node down into every column. */}
          {steps.map((step, i) => {
            if (step.type !== 'switch') return null;
            return (
              <div
                key={`switch-${i}`}
                // NOT data-no-pan: the fan is huge (branches + flaired arms), and blanketing it would kill
                // panning across all that empty space. Its interactive bits (range chips, wait inputs, "+"
                // pickers, remove ✕) are buttons/inputs that suppress pan on their own; empty gutters pan.
                className={cn(
                  'absolute',
                  dragSpineIndex === i && 'pointer-events-none opacity-30',
                  switchPickerOpen === i ? 'z-50' : 'z-10',
                )}
                style={{ left: switchFanLeft(CX), top: nodeCenterY(i) + SWITCH_BELOW, transform: 'translate(0, 0)' }}>
                <SequenceSwitchBranches
                  step={step}
                  switchIndex={i}
                  onStepDrag={readOnly ? undefined : startStepDrag}
                  onChange={(next) => updateStepAt(i, () => next)}
                  onRemove={readOnly ? undefined : () => remove(i)}
                  onEditBranchStep={(branch, arm) => openEditor({ kind: 'switchArm', group: i, branch, arm })}
                  onEditBranchContainerStep={(branch, slot, arm) =>
                    openEditor({ kind: 'switchArmChild', group: i, branch, slot, arm })
                  }
                  autoOpenPicker={autoOpenContainerAt === i}
                  onPickerOpenChange={(open) => setSwitchPickerOpen(open ? i : null)}
                  readOnly={readOnly}
                />
              </div>
            );
          })}

          {/* Step content editor — a PORTALED popover anchored (Floating UI) to the clicked node, so it
              always opens right next to that exact node, on-screen, at natural size, no matter how deep it
              is nested or how zoomed the canvas is. Commits on close (Escape / outside). In readOnly mode it
              edits THIS contact's override (body/subject only), not the template. */}
          {editing != null && editorStep != null && (
            <FloatingPortal>
              <div ref={editorFloat.refs.setFloating} data-no-pan data-step-editor className="z-50" style={editorFloat.floatingStyles}>
                <SequenceStepEditor
                  key={
                    editing.kind === 'spine'
                      ? `s${editing.index}`
                      : editing.kind === 'spawnArm'
                        ? `sp${editing.group}-${editing.arm}`
                        : editing.kind === 'switchArm'
                          ? `sw${editing.group}-${editing.branch}-${editing.arm}`
                          : editing.kind === 'switchArmChild'
                            ? `swc${editing.group}-${editing.branch}-${editing.slot}-${editing.arm}`
                            : `a${editing.group}-${editing.arm}`
                  }
                  step={editorStep}
                  onChange={updateEditingStepLocal}
                  onClose={closeEditor}
                  variables={variables}
                  documents={documents}
                  reminderActions={reminderActions}
                  reminderActionsLoading={reminderActionsLoading}
                  onCreateReminder={onCreateReminder}
                  reminderCreating={reminderCreating}
                  contentOnly={readOnly}
                  previousEmailSubject={editingPrevEmailSubject}
                />
              </div>
            </FloatingPortal>
          )}

          {n === 0 && !readOnly && (
            <div className="absolute" style={{ left: CX, top: nodeCenterY(0), transform: 'translate(-50%, -50%)' }}>
              {/* The hint is a second trigger for the leading "+": clicking it opens the picker, hovering it
                  animates the "+" (via firstStepHintHovered → SequenceGraphInsert `hovered`). */}
              <Button
                text
                data-no-pan
                onClick={() => setPickerAt(0)}
                onMouseEnter={() => setFirstStepHintHovered(true)}
                onMouseLeave={() => setFirstStepHintHovered(false)}
                className="whitespace-nowrap text-sm font-normal text-gray-500 hover:text-blue-500 dark:text-gray-400 dark:hover:text-blue-400">
                Click + to add your first step
              </Button>
            </div>
          )}
        </div>
      </SequencePanZoom>

      <div className="pointer-events-none absolute right-3 top-3 z-10 flex h-5 items-center">
        {updateMutation.isPending && <span className="text-xs text-gray-400">Saving…</span>}
        {updateMutation.isError && <span className="text-xs text-red-500">Couldn&apos;t save — try again</span>}
      </div>

      {/* Catch-all pile for people parked in a state no authored step places on the canvas (shared park
          lanes, needs-attention, an unmodelled node). Keeps the editor honest — every enrolled contact is
          reachable here even when their state isn't tied to one step. */}
      {offGraph.total > 0 && (
        <div className="absolute bottom-3 right-3 z-10 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50/95 px-2.5 py-1.5 shadow-sm dark:border-amber-700 dark:bg-amber-950/80">
          <Tooltip tooltip="People parked in a state this view doesn't place on a step — e.g. reconnect LinkedIn, waiting for working hours, or needs attention. They're still enrolled; open one to see exactly where they are.">
            <span className="text-xs font-medium text-amber-700 dark:text-amber-300">Other states</span>
          </Tooltip>
          <SequenceNodeFaces
            sample={offGraph.sample}
            total={offGraph.total}
            onOpenContact={onOpenContactFace}
            onRestart={onRestartFace}
            onRemove={onRemoveFace}
            onCompleteTask={onCompleteTaskFace}
          />
        </div>
      )}

      {/* Drag ghost — a screen-space copy of the dragged node that tracks the cursor. Portaled to the
          body so no pan/zoom transform (or overflow clip) affects it; pointer-events-none so it never
          blocks the drop hit-test underneath. */}
      {draggedStep != null &&
        dragArmed &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={ghostRef}
            className="pointer-events-none fixed left-0 top-0 z-[60] opacity-90"
            style={{
              transform: `translate(${pointerRef.current.x}px, ${pointerRef.current.y}px) translate(-50%, -50%)`,
            }}>
            <SequenceNodePreview step={draggedStep} />
          </div>,
          document.body,
        )}

      {/* Reorder insertion bar — a screen-space line placed imperatively at the target gap during a drag (in
          ANY surface). Hidden by default; the move handler positions + shows it. Portaled so no pan/zoom
          transform affects it. */}
      {typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={dropBarRef}
            className="pointer-events-none fixed z-[55] h-0.5 rounded-full bg-blue-500"
            style={{ display: 'none' }}
          />,
          document.body,
        )}
    </div>
  );
}
